import { intersection } from "ramda";
import { type FC, useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { ActionButtonIntent, HeaderDivider, LoadingSpinner, TriggerButton, TriggerButtonIntent } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { matchFilter, parseFilterExpression } from "../../app-utils/filter.js";
import { type ISelectionModel } from "../../app-utils/selection-model.js";
import { pluralizeWord } from "../../app-utils/string.js";
import { type PlaybookCategory } from "../../graphql.js";
import { deleteAdminPlaybookCategory, loadAdminPlaybookCategories } from "../../redux/modules/playbook-admin.js";
import { type IIdentifiable } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import { AsyncSearchWithExprParser } from "../core/forms/AsyncSearchWithExprParser.js";
import DataContextIndicator from "../core/grid/DataContextIndicator.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import { Dialog } from "../core/overlays/Dialogs.js";
import { ModalActionWidget, ModalControlledOverlay } from "../core/overlays/index.js";
import PlaybookCategoryEditDialog from "./PlaybookCategoryEditDialog.js";
import { CELL_TYPE } from "@/settings/settings.ts";
import { useAsyncFilter } from "@/ui-modules/core/forms/hooks/useAsyncFilter.js";

const adminPlaybookCategoriesColumnDefs: any[] = [
  {
    name: "Image",
    field: "category.imageName",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Name",
    field: "category.name",
    cellType: CELL_TYPE.STRING,
  },
  {
    name: "Protected",
    field: "category.protectedCategory",
    cellType: CELL_TYPE.BOOLEAN,
  },
  {
    name: "Playbooks",
    field: "category.playbooks.name",
    cellType: CELL_TYPE.STRING_LIST,
  },
];

const AdminPlaybookCategoriesTable: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [rowData, setRowData] = useState(null);
  const [categoryToEdit, setCategoryToEdit] = useState<PlaybookCategory>();
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [disabledDeleteButtonMessage, setDisabledDeleteButtonMessage] = useState<string>(null);
  const [selectedIdsToDelete, setSelectedIdsToDelete] = useState<number[]>([]);
  const [filterTerm, setFilterTerm] = useAsyncFilter(null);

  const dataTableRef = useRef<InMemoryTableWithSelectionModel>();

  const isLoadingPlaybookCategories = useSelector<IAppState, boolean>(
    (state) => state.playbookAdminState.isLoadingPlaybookCategories,
    shallowEqual,
  );
  const categories = useSelector<IAppState, PlaybookCategory[]>(
    (state) => state.playbookAdminState.adminPlaybookCategories,
    shallowEqual,
  );

  const handleCategoryClicked = (category: PlaybookCategory) => {
    setCategoryToEdit(category);
    setShowEditDialog(true);
  };

  const onEditDialogClose = () => {
    setShowEditDialog(false);
    setCategoryToEdit(null);
  };

  const cellRendererParams = {
    "category.name": {
      handleCategoryClicked: handleCategoryClicked,
    },
  };

  useEffect(() => {
    dispatch(loadAdminPlaybookCategories({ showAlert: true }));
  }, [dispatch]);

  useEffect(() => {
    setRowData(createPlaybookCategoriesRowData(filterTerm, categories));
  }, [categories, filterTerm]);

  useEffect(() => {
    if (!selectedIdsToDelete?.length) {
      setDisabledDeleteButtonMessage("At least one category must be selected to perform the deletion.");
      return;
    }
    const protectedCategories = categories
      .filter((category) => category.protectedCategory)
      .map((category) => category.id);

    const containsProtected = !!intersection(protectedCategories, selectedIdsToDelete).length;

    if (containsProtected) {
      setDisabledDeleteButtonMessage("Protected categories cannot be deleted.");
    } else {
      setDisabledDeleteButtonMessage(null);
    }
  }, [selectedIdsToDelete]);

  return (
    <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
      <PageHeader
        title={"Categories"}
        badge={String(categories.length)}
        toolsWidgets={[
          <AsyncSearchWithExprParser
            onSearchExprChange={setFilterTerm}
            controlledValue={filterTerm}
            key={"playbook-categories-admin-page-widget-1"}
          />,
        ]}
        actionWidgets={[
          <ModalActionWidget
            actionWidget={({ open }) => (
              <TriggerButton
                id={"sample-type-admin-delete"}
                tooltip={`Delete the selected ${pluralizeWord("category", selectedIdsToDelete?.length)}`}
                onPress={() => open()}
                isDisabled={disabledDeleteButtonMessage}
                intent={TriggerButtonIntent.Danger}
                icon={"delete"}
                label={"Delete"}
                showLabel
              />
            )}
          >
            {({ close }) => (
              <CategoryDeletionConfirmationDialog
                categoryIds={selectedIdsToDelete}
                onClose={close}
                onDelete={() => {
                  setSelectedIdsToDelete([]);
                  dataTableRef.current.resetSelection();
                }}
              />
            )}
          </ModalActionWidget>,
          <HeaderDivider />,
          <TriggerButton
            id={"playbook-categories-admin-page-action-1"}
            tooltip={"Create a new Playbook Category"}
            onPress={() => setShowEditDialog(true)}
            icon={"add_circle"}
            label={"New Category"}
            showLabel
          />,
        ]}
      />
      {isLoadingPlaybookCategories ? (
        <LoadingSpinner inCenter />
      ) : (
        <div style={{ height: "calc(100% - 50px)" }}>
          <InMemoryTableWithSelectionModel
            columns={adminPlaybookCategoriesColumnDefs}
            ref={dataTableRef}
            rowGridData={rowData}
            useSelectionModel={true}
            isLoading={isLoadingPlaybookCategories}
            uniqueGridIdentifier={"admin-playbook-categories"}
            cellRendererParamsLookup={cellRendererParams}
            selectionModelUpdated={(selectionModel: ISelectionModel) => {
              setSelectedIdsToDelete(selectionModel.selectedIds.map(Number));
            }}
          />
          <DataContextIndicator
            key="context-indicator"
            numRowsSelected={selectedIdsToDelete.length}
            numTotalRows={categories.length}
            numRowsFiltered={rowData?.length}
            rowEntityLabel={"Category"}
            filterIsSet={!!filterTerm?.length}
            onResetFilter={() => setFilterTerm("")}
          />
        </div>
      )}

      {showEditDialog && (
        <ModalControlledOverlay isOpen={true} close={onEditDialogClose}>
          {({ close }) => <PlaybookCategoryEditDialog playbookCategory={categoryToEdit} onClose={close} />}
        </ModalControlledOverlay>
      )}
    </div>
  );
};

const createPlaybookCategoriesRowData = (filterTerm: string, categories: PlaybookCategory[]): IIdentifiable[] => {
  return categories
    .filter((category: PlaybookCategory) =>
      matchFilter(
        [category.name, category.playbooks.flatMap((playbook) => playbook.name).join(", ")],
        parseFilterExpression(filterTerm),
      ),
    )
    .map((category: PlaybookCategory) => {
      return {
        id: category.id,
        category: category,
      };
    });
};

interface ICategoryDeletionConfirmationDialogProps {
  categoryIds: number[];
  onClose: () => void;
  onDelete: () => void;
}

const CategoryDeletionConfirmationDialog: FC<ICategoryDeletionConfirmationDialogProps> = (props) => {
  const { categoryIds, onClose, onDelete } = props;
  const dispatch = useDispatch<ReduxDispatch>();
  const [deletionError, setDeletionError] = useState<string>(null);

  const handleDelete = (): Promise<any> => {
    return dispatch(deleteAdminPlaybookCategory(categoryIds))
      .then(() => {
        onDelete();
        onClose();
      })
      .catch((rejected: Error) => {
        setDeletionError(rejected.message);
      });
  };

  return (
    <Dialog
      closeFn={onClose}
      headerProps={{
        title: `Delete Playbook ${pluralizeWord("Category", categoryIds.length)}`,
      }}
      noHeaderActionWidgets
      mainActionButtonProps={{
        children: "Delete",
        intent: ActionButtonIntent.Danger,
        onPress: handleDelete,
      }}
      footerMessage={{ message: deletionError }}
    >
      <strong>Do you really want to delete the selected {pluralizeWord("category", categoryIds.length)}?</strong>
    </Dialog>
  );
};

export { AdminPlaybookCategoriesTable };
